import { Address } from '@synergy/models';
import { Component, ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import { Grid } from '@mui/material';
import { Tooltip } from '@mui/material';
import { connect } from 'react-redux';
import { debounce } from 'lodash';
import { getAddressDisplayValue, padZeroLeft } from '@synergy/utils';

import { AppState } from '../../../../store/models/app-state';
import { Contact } from '../../../models/contact';
import { Customer } from '../../../models/customer';
import { PoolAutocompleteList } from '../../../components/autocomplete-list/pool-autocomplete-list';
import { fetchCustomer, resetCustomer } from '../../../actions/customer-actions';
import { quickSearchCustomers, resetQuickSearch } from '../../../actions/quick-search-actions';

interface QuickSearchOwnProps {
    inputRef?: (ref: HTMLInputElement) => void;
    onCustomerSelected?: (customer: Customer, contact: Contact | undefined, address: Address) => void;
    /**
     * One row per customer and address instead of one per contact as well:
     * the same company stood there once for every contact (R-225). The
     * contact comes along only where there is one.
     */
    onePerAddress?: boolean;
    disabled?: boolean;
    id?: string;
    /**
     * Replaces the default "Schnellsuche" label; null for a field that has its
     * own, which also lets the placeholder show.
     */
    label?: ReactNode;
    placeholder?: string;
    autoFocus?: boolean;
    /** Renders the bare input, for a cell of a form grid rather than a row of its own. */
    inline?: boolean;
}

interface QuickSearchState {
    selectedCustomerId?: number;
    selectedContact?: Contact;
    selectedAddress?: Address;
}

interface QuickSearchReduxProps {
    customers: Customer[];
    selectedCustomer: Customer | undefined;
    inProgress: boolean;
    quickSearchCustomers: (searchParams: URLSearchParams) => void;
    resetQuickSearch: () => void;
    resetCustomer: () => void;
    getCustomer: (id: number) => void;
}

type QuickSearchProps = QuickSearchOwnProps & QuickSearchReduxProps;

interface ExtendedCustomer {
    id: string;
    name: string;
    address: string;
    contact: ReactNode;
    tags: string;
    extendedData: {
        customer: Customer;
        contact?: Contact;
        address: Address;
    };
}

class QuickSearchBase extends Component<QuickSearchProps, QuickSearchState> {
    constructor(props: QuickSearchProps) {
        super(props);
        this.handleContactSearchChange = debounce(this.handleContactSearchChange, 800);
    }

    private getSuggestionValue = (suggestion: ExtendedCustomer) =>
        suggestion &&
        (this.props.onePerAddress
            ? `${suggestion.name} \u2022 ${suggestion.address}`
            : `${suggestion.name} \u2022 ${suggestion.address} \u2022 ${suggestion.contact} \u2022 ${suggestion.tags}`);

    /**
     * The contacts behind an address as a hint: the first and how many more,
     * all of them on hover. Written out in full they made the list 2.500
     * pixels wide (R-225).
     */
    private contactHint = (contacts: Contact[]): ReactNode => {
        if (contacts.length < 2) {
            return contacts[0]?.fullName ?? '';
        }

        return (
            <Tooltip
                title={contacts.map(contact => contact.fullName).join(', ')}
                placement="top"
                // Above the list it belongs to, which sits at 99999 (R-225).
                slotProps={{ popper: { sx: { zIndex: 100000 } } }}
            >
                <span style={{ whiteSpace: 'nowrap' }}>
                    {contacts[0].fullName} + {contacts.length - 1}
                </span>
            </Tooltip>
        );
    };

    private handleCustomerSelected = (selectedContact: ExtendedCustomer) => {
        if (selectedContact) {
            const id = parseInt(selectedContact.id, 10);
            if (id) {
                this.setState({
                    selectedCustomerId: id,
                    selectedContact: selectedContact.extendedData.contact,
                    selectedAddress: selectedContact.extendedData.address,
                });
                this.props.getCustomer(id);
            }
        }
    };

    private handleContactSearchChange = (customerSearch: string) => {
        this.props.quickSearchCustomers(new URLSearchParams({ search: customerSearch }));
    };

    private getSearchResult = () => {
        const { customers } = this.props;

        if (this.props.onePerAddress) {
            return customers.flatMap(customer =>
                (customer.addresses ?? []).map(address => ({
                    id: padZeroLeft(customer.id, 5),
                    name: customer.name,
                    address: getAddressDisplayValue(address),
                    contact: this.contactHint(customer.contacts ?? []),
                    tags: (customer.tags || []).join(' '),
                    extendedData: {
                        customer,
                        contact: 1 === customer.contacts?.length ? customer.contacts[0] : undefined,
                        address,
                    },
                }))
            );
        }

        // Add multiple results per address and contact
        const result: ExtendedCustomer[] = [];
        customers.forEach(customer => {
            if (customer.addresses) {
                customer.addresses.forEach(address => {
                    if (customer.contacts) {
                        customer.contacts.forEach(contact => {
                            const extendedCustomer = {
                                id: padZeroLeft(customer.id, 5),
                                name: customer.name,
                                address: getAddressDisplayValue(address),
                                contact: contact.fullName,
                                tags: [...(customer.tags || []), ...(contact.tags || [])].join(' '),
                                extendedData: {
                                    customer,
                                    contact,
                                    address,
                                },
                            };
                            result.push(extendedCustomer);
                        });
                    }
                });
            }
        });
        return result;
    };

    private resetState() {
        this.setState({ selectedAddress: undefined, selectedContact: undefined, selectedCustomerId: undefined });
    }

    public componentDidMount() {
        this.props.resetCustomer();
        this.props.resetQuickSearch();
    }

    public UNSAFE_componentWillReceiveProps(nextProps: QuickSearchProps) {
        if (this.state) {
            if (nextProps.selectedCustomer && nextProps.selectedCustomer.id === this.state.selectedCustomerId) {
                if (nextProps.onCustomerSelected) {
                    if (
                        nextProps.selectedCustomer &&
                        (this.state.selectedContact || this.props.onePerAddress) &&
                        this.state.selectedAddress
                    ) {
                        nextProps.onCustomerSelected(
                            nextProps.selectedCustomer,
                            this.state.selectedContact,
                            this.state.selectedAddress
                        );
                    }
                }
                this.resetState();
                nextProps.resetCustomer();
                nextProps.resetQuickSearch();
            }
        }
    }

    public render() {
        const input = (
            <PoolAutocompleteList
                id={this.props.id || ''}
                inputRef={this.props.inputRef}
                data={this.getSearchResult()}
                getSuggestionValue={suggestion => this.getSuggestionValue(suggestion as ExtendedCustomer)}
                label={
                    this.props.label === undefined ? <FormattedMessage id="app.common.quickSearch" /> : this.props.label
                }
                placeholder={this.props.placeholder}
                autoFocus={this.props.autoFocus}
                handleSuggestionSelected={suggestion => this.handleCustomerSelected(suggestion as ExtendedCustomer)}
                clearInputOnSelect={true}
                onValueChanged={this.handleContactSearchChange}
                initialValue={''}
                exclude={['addresses', 'contacts', 'isContractPartner', 'type', 'extendedData']}
                truncate={this.props.onePerAddress ? ['address'] : undefined}
                disabled={this.props.disabled}
                showProgress={this.props.inProgress}
            />
        );

        if (this.props.inline) {
            return input;
        }

        return (
            <Grid container={true} spacing={10}>
                <Grid item={true} xs={12}>
                    {input}
                </Grid>
            </Grid>
        );
    }
}

const mapStateToProps = (state: AppState) => ({
    customers: state.quickSearch.flat,
    selectedCustomer: state.customers.single,
    inProgress: state.quickSearch.inProgress,
});

export const QuickSearch = connect(mapStateToProps, {
    quickSearchCustomers,
    resetQuickSearch,
    resetCustomer,
    getCustomer: fetchCustomer,
})(QuickSearchBase);
